/**
 * Test-drive check-in: licence → agreement & documents → keys.
 *
 * Nothing is uploaded until the consultant presses "Hand over the keys", so an
 * abandoned check-in leaves no files behind. On submit the captures are
 * uploaded one by one, then a single call starts the drive:
 *
 *   appointment mode  POST /v1/test-drives/from-appointment  (creates the drive)
 *   drive mode        PATCH licence → POST sign → POST start  (drive already PLANNED)
 *
 * The backend refuses to release a car without a licence photo, a signed
 * agreement (on-screen signature, or a named consultant attesting a paper
 * form) and a declared insurance position — this screen just makes all three
 * quick to collect.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { testDrivesService } from '../../lib/services';
import { useToast } from '../../lib/toast';
import { apiErrorMessage } from '../../lib/apiError';
import { TEST_DRIVE_DOCUMENTS } from '../../lib/testDriveDocuments';
import CameraCapture from './CameraCapture';
import SignaturePad from './SignaturePad';

// "Agreement & documents" merges the old Documents and Agreement steps: the
// customer reads the dealership's documents, accepts and signs in one place, and
// any extra paperwork (a scanned paper form, proof of address) is attached right
// beside it instead of on a separate step few people used.
const STEPS = ['Licence', 'Agreement & documents', 'Keys'];
const AGREEMENT_STEP = 1;
const KEYS_STEP = 2;

function todayISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

export default function TestDriveCheckInModal({
  appointmentId,          // appointment mode
  drive,                  // drive mode (PLANNED test drive)
  scheduledAt,            // the booking's scheduled start — for the early-start guard
  customerName,
  vehicleLabel,
  onClose,
  onDone,
}) {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  // Starting a drive before its booked time is allowed, but must be an explicit
  // choice (DEF: a future drive was checked in without any warning). "Early" is
  // judged from when the check-in was OPENED, so it doesn't quietly flip to
  // false while the consultant works through the steps. Comparison is on absolute
  // instants (UTC), so the Sydney display vs the device clock is irrelevant.
  const [openedAt] = useState(() => Date.now());
  const scheduledStart = scheduledAt || drive?.scheduledAt || drive?.startsAt || null;
  const scheduledMs = scheduledStart ? new Date(scheduledStart).getTime() : NaN;
  const earlyMs = Number.isFinite(scheduledMs) ? scheduledMs - openedAt : 0;
  const startingEarly = earlyMs > 60 * 1000;   // more than a minute before the booked time
  const [earlyAck, setEarlyAck] = useState(false);
  const [progress, setProgress] = useState('');

  // licence
  const [licFront, setLicFront] = useState(null);
  const [licBack, setLicBack] = useState(null);
  const [licNumber, setLicNumber] = useState('');
  const [licExpiry, setLicExpiry] = useState('');
  // idle | loading | done | unreadable (couldn't check — never blocks) |
  // rejected (Gemini confidently says this isn't a licence — blocks Next)
  const [ocrState, setOcrState] = useState('idle');
  const [ocrNote, setOcrNote] = useState('');
  // Fallback ONLY: when the front is rejected, check the back photo purely
  // to confirm it's a genuine licence — it never supplies licNumber/licExpiry.
  // Those two fields come from the front only, always.
  const [backOcrState, setBackOcrState] = useState('idle');
  const [backOcrNote, setBackOcrNote] = useState('');
  // extra documents the consultant attaches (optional)
  const [docs, setDocs] = useState([]);   // [{ file, label }]
  // Which of the dealership's documents the customer has opened on this step.
  // Shown as ticks only — it does not gate Next.
  const [viewed, setViewed] = useState(() => new Set());
  // The document currently open in the in-modal viewer (null = none).
  const [viewing, setViewing] = useState(null);
  // agreement
  const [mode, setMode] = useState('SIGN');   // SIGN | PAPER
  // Only prefill a real name — the directory falls back to labels like
  // "Customer 1c4de68a…" when a customer has no name on file.
  const realName = customerName && customerName !== '—'
    && !/^Customer [0-9a-f]{6,}/i.test(customerName) && !customerName.includes('…');
  const [signerName, setSignerName] = useState(realName ? customerName : '');
  const [accepted, setAccepted] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [paperHeld, setPaperHeld] = useState(false);
  const padRef = useRef(null);
  const [sigBlob, setSigBlob] = useState(null);
  // keys
  const [kmOut, setKmOut] = useState('');
  const [plates, setPlates] = useState([]);
  const [plateId, setPlateId] = useState('');
  const [insurance, setInsurance] = useState(false);

  useEffect(() => {
    if (!licFront) { setOcrState('idle'); setOcrNote(''); return; }
    let cancelled = false;
    setOcrState('loading');
    setOcrNote('');
    testDrivesService.ocrLicence(licFront)
      .then((r) => {
        if (cancelled) return;
        if (r?.isLicence) {
          if (r.licenceNumber) setLicNumber(r.licenceNumber);
          if (r.expiryDate) setLicExpiry(r.expiryDate);
          setOcrState('done');
        } else if (r?.evaluated) {
          // Gemini actually looked at it and says it isn't a licence.
          setOcrState('rejected');
          setOcrNote(r?.reason || 'Upload a correct licence photo to continue.');
        } else {
          // Couldn't even check (feature off, bad file, Gemini unreachable) —
          // never block on this, just let the consultant type it manually.
          setOcrState('unreadable');
          setOcrNote(r?.reason || 'Couldn’t read this automatically — enter the details manually.');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setOcrState('unreadable');
        setOcrNote('Couldn’t read this automatically — enter the details manually.');
      });
    return () => { cancelled = true; };
  }, [licFront]);

  // Fallback: only runs when the front was rejected AND a back photo exists.
  // Purely a second chance to confirm this is a genuine licence — never
  // touches licNumber/licExpiry, since that data only ever comes off the
  // front photo.
  useEffect(() => {
    if (ocrState !== 'rejected' || !licBack) {
      setBackOcrState('idle');
      setBackOcrNote('');
      return;
    }
    let cancelled = false;
    setBackOcrState('loading');
    setBackOcrNote('');
    testDrivesService.ocrLicence(licBack)
      .then((r) => {
        if (cancelled) return;
        if (r?.isLicence) {
          setBackOcrState('done');
        } else if (r?.evaluated) {
          setBackOcrState('rejected');
          setBackOcrNote('Neither photo looks like a licence — please upload a correct one.');
        } else {
          setBackOcrState('unreadable');
          setBackOcrNote('Couldn’t check the back photo either — upload a correct front photo to continue.');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setBackOcrState('unreadable');
        setBackOcrNote('Couldn’t check the back photo either — upload a correct front photo to continue.');
      });
    return () => { cancelled = true; };
  }, [ocrState, licBack]);

  useEffect(() => {
    testDrivesService.tradePlates()
      .then((d) => {
        const list = Array.isArray(d) ? d : (d?.content || d?.items || []);
        setPlates(list.filter((p) => p.status === 'AVAILABLE'));
      })
      .catch(() => setPlates([]));
  }, []);

  useEffect(() => {
    // Esc closes the document viewer first, the whole check-in only when no
    // document is open.
    const onKey = (e) => {
      if (e.key !== 'Escape' || busy) return;
      if (viewing) setViewing(null); else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose, viewing]);

  const sigUrl = useMemo(() => (sigBlob ? URL.createObjectURL(sigBlob) : null), [sigBlob]);
  useEffect(() => () => { if (sigUrl) URL.revokeObjectURL(sigUrl); }, [sigUrl]);

  const alreadySigned = drive?.agreementStatus === 'SIGNED';
  // On-screen signing requires every document to have been opened first. Not
  // applied to paper-form signing or an already-signed drive — in both the
  // customer has dealt with the paperwork outside this screen.
  const allViewed = TEST_DRIVE_DOCUMENTS.every((d) => viewed.has(d.id));
  const viewedCount = TEST_DRIVE_DOCUMENTS.filter((d) => viewed.has(d.id)).length;
  const expired = licExpiry && licExpiry < todayISO();
  // Native date inputs accept 5–6 digit years (e.g. 275760). Validate the year
  // numerically (string compare breaks for mismatched digit counts): a licence
  // expiry must be a 4-digit year within the next 20 years.
  const curYear = new Date().getFullYear();
  const expYear = licExpiry ? Number(String(licExpiry).split('-')[0]) : null;
  const expiryBad = !!licExpiry
    && (!Number.isInteger(expYear) || expYear < curYear || expYear > curYear + 20);
  const maxExpiryISO = `${curYear + 20}-12-31`;
  const tooBig = docs.some((d) => d.file.size > 10 * 1024 * 1024);
  // The back fallback only ever exists to clear a front rejection — a front
  // that passes (or hasn't been judged yet) never looks at the back at all.
  const frontRejected = ocrState === 'rejected';
  const backFallbackPassed = frontRejected && !!licBack && backOcrState === 'done';
  const backFallbackPending = frontRejected && !!licBack && backOcrState === 'loading';
  const stepValid = [
    !!licFront && !expired && !expiryBad && ocrState !== 'loading' && !backFallbackPending
      && (!frontRejected || backFallbackPassed),
    // Agreement & documents. Next needs the "read and accepts" tick (on-screen
    // signing) or the paper-form tick, plus the signature itself — the backend
    // refuses to release a car without a signed agreement.
    !tooBig && (alreadySigned
      ? true
      : mode === 'SIGN'
        ? (allViewed && accepted && signerName.trim().length > 1 && (!sigEmpty || !!sigBlob))
        : paperHeld),
    kmOut !== '' && !Number.isNaN(Number(kmOut)) && Number(kmOut) >= 0 && insurance,
  ];

  const next = async () => {
    if (!stepValid[step]) return;
    // Freeze the signature before the pad unmounts.
    if (step === AGREEMENT_STEP && mode === 'SIGN' && !alreadySigned && !sigBlob && !sigEmpty) {
      const b = await padRef.current?.toBlob();
      if (b) setSigBlob(b);
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const addDocs = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setDocs((d) => [...d, ...files.map((file) => ({ file, label: file.name.replace(/\.\w+$/, '') }))]);
  };

  const submit = async () => {
    if (!stepValid.every(Boolean)) return;
    setBusy(true);
    const target = drive ? { testDriveId: drive.id } : { appointmentId };
    try {
      setProgress('Uploading licence…');
      const front = await testDrivesService.uploadCapture({ ...target, file: licFront, kind: 'LICENCE_FRONT', label: 'Licence (front)' });
      const backCap = licBack
        ? await testDrivesService.uploadCapture({ ...target, file: licBack, kind: 'LICENCE_BACK', label: 'Licence (back)' })
        : null;

      const documentIds = [];
      for (let i = 0; i < docs.length; i += 1) {
        setProgress(`Uploading document ${i + 1} of ${docs.length}…`);
        const c = await testDrivesService.uploadCapture({ ...target, file: docs[i].file, kind: 'DOCUMENT', label: docs[i].label });
        documentIds.push(c.id);
      }

      let signatureId = null;
      if (mode === 'SIGN' && !alreadySigned) {
        setProgress('Saving signature…');
        const blob = sigBlob || await padRef.current?.toBlob();
        if (!blob) throw new Error('The signature is empty — ask the customer to sign again.');
        const sig = await testDrivesService.uploadCapture({
          ...target, file: blob, fileName: 'signature.png', kind: 'SIGNATURE',
          signerName: signerName.trim(),
          // Records WHICH documents were accepted alongside the signature.
          label: `Test-drive agreement — accepted: ${TEST_DRIVE_DOCUMENTS.map((d) => d.short).join(', ')}`,
        });
        signatureId = sig.id;
      }

      setProgress('Releasing the car…');
      if (drive) {
        await testDrivesService.update(drive.id, {
          licenceDocumentId: front.id,
          licenceBackDocumentId: backCap?.id || null,
          licenceNumber: licNumber.trim() || null,
          licenceExpiry: licExpiry || null,
          insuranceDeclared: true,
        });
        if (signatureId) {
          await testDrivesService.sign(drive.id, { signatureDocumentId: signatureId });
        } else if (drive.agreementStatus !== 'SIGNED') {
          throw new Error('This drive has no signed agreement — capture the customer’s signature.');
        }
        await testDrivesService.start(drive.id, { kmOut: Number(kmOut), tradePlateId: plateId || null });
      } else {
        await testDrivesService.startFromAppointment({
          appointmentId,
          kmOut: Number(kmOut),
          tradePlateId: plateId || null,
          insuranceDeclared: true,
          agreementHeld: mode === 'PAPER' ? paperHeld : false,
          licenceFrontId: front.id,
          licenceBackId: backCap?.id || null,
          licenceNumber: licNumber.trim() || null,
          licenceExpiry: licExpiry || null,
          signatureId,
          documentIds,
        });
      }
      toast('Test drive started — the car is out', { variant: 'success' });
      onDone?.();
      onClose();
    } catch (e) {
      toast(apiErrorMessage(e, 'Could not start the test drive'), { variant: 'danger' });
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  const tick = (checked, set, text, locked = false) => (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13,
      cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.6 : 1 }}>
      <input type="checkbox" checked={checked} disabled={busy || locked} onChange={(e) => set(e.target.checked)} />
      <span>{text}</span>
    </label>
  );

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Test drive check-in"
      onClick={() => { if (!busy) onClose(); }} style={{ zIndex: 300 }}>
      <div className="modal" style={{ width: 'min(620px, 96vw)', maxHeight: '94vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Start test drive</div>
            <div className="mono text-xs text-muted">
              {[customerName, vehicleLabel].filter((x) => x && x !== '—').join(' · ') || 'Check-in'}
            </div>
          </div>
          <button type="button" className="modal-close" disabled={busy} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* stepper */}
        <div style={{ display: 'flex', gap: 6, padding: '12px 20px 0' }}>
          {STEPS.map((s, i) => (
            <button key={s} type="button" disabled={busy || i > step}
              onClick={() => setStep(i)}
              className={`pill ${i === step ? 'filled' : ''}`}
              style={{ flex: 1, justifyContent: 'center', opacity: i > step ? 0.5 : 1, cursor: i <= step ? 'pointer' : 'default' }}>
              {i < step ? '✓ ' : `${i + 1}. `}{s}
            </button>
          ))}
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {startingEarly && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12,
              border: '1px solid var(--warning, #d69e2e)', borderRadius: 8, padding: '8px 10px',
              background: 'color-mix(in srgb, var(--warning, #d69e2e) 12%, transparent)' }}>
              <span aria-hidden>⏰</span>
              <span>
                <strong>Starting early.</strong> This drive is booked for{' '}
                <strong>{new Date(scheduledStart).toLocaleString('en-AU',
                  { timeZone: 'Australia/Sydney', weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</strong>
                {' '}(AU time). Confirm on the last step before handing over the keys.
              </span>
            </div>
          )}
          {step === 0 && (
            <>
              <div className="text-xs text-muted">
                Photograph the customer&rsquo;s driver licence. The image is stored privately and only a
                dealer principal or sales manager can view it later.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                <CameraCapture label="Licence — front" required value={licFront} onChange={setLicFront} disabled={busy} />
                <CameraCapture label="Licence — back (optional)" value={licBack} onChange={setLicBack} disabled={busy} />
              </div>
              {ocrState === 'loading' && (
                <div className="mono text-xs text-muted">Reading licence…</div>
              )}
              {ocrState === 'done' && (
                <div className="mono text-xs" style={{ color: 'var(--success, #16a34a)' }}>
                  ✓ Filled in from the photo — check it&rsquo;s correct before continuing.
                </div>
              )}
              {ocrState === 'unreadable' && (
                <div className="mono text-xs text-muted">{ocrNote}</div>
              )}
              {frontRejected && !licBack && (
                <div className="text-xs" style={{ color: 'var(--danger, #c53030)', fontWeight: 600 }}>
                  ✕ {ocrNote || 'Upload a correct licence photo to continue.'} Or add a back photo — it&rsquo;ll be
                  checked as a fallback.
                </div>
              )}
              {frontRejected && licBack && backOcrState === 'loading' && (
                <div className="mono text-xs text-muted">Front didn&rsquo;t check out — checking the back photo instead…</div>
              )}
              {backFallbackPassed && (
                <div className="mono text-xs" style={{ color: 'var(--success, #16a34a)' }}>
                  ✓ The front photo couldn&rsquo;t be read, but the back was confirmed as a genuine licence — you can
                  continue. Licence number and expiry still need to be entered manually, since those only come off
                  the front.
                </div>
              )}
              {frontRejected && licBack && (backOcrState === 'rejected' || backOcrState === 'unreadable') && (
                <div className="text-xs" style={{ color: 'var(--danger, #c53030)', fontWeight: 600 }}>
                  ✕ {backOcrNote}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-field">
                  <label htmlFor="ci-lic-no">Licence number</label>
                  <input id="ci-lic-no" value={licNumber} autoComplete="off" disabled={busy}
                    onChange={(e) => setLicNumber(e.target.value)} placeholder="Stored masked" />
                </div>
                <div className="form-field">
                  <label htmlFor="ci-lic-exp">Expiry</label>
                  <input id="ci-lic-exp" type="date" value={licExpiry} disabled={busy}
                    min={todayISO()} max={maxExpiryISO}
                    onChange={(e) => setLicExpiry(e.target.value)} />
                </div>
              </div>
              {expired && !expiryBad && (
                <div className="text-xs" style={{ color: 'var(--danger, #c53030)' }}>
                  This licence has expired — the car cannot be released.
                </div>
              )}
              {expiryBad && (
                <div className="text-xs" style={{ color: 'var(--danger, #c53030)' }}>
                  Enter a valid expiry date — a 4-digit year within the next 20 years.
                </div>
              )}
            </>
          )}

          {step === AGREEMENT_STEP && (
            <>
              {/* 1 — the dealership's documents, read by the customer */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Documents to read</div>
                <div className="text-xs text-muted" style={{ marginTop: 2 }}>
                  Ask the customer to open and read each document before accepting.
                </div>
              </div>
              <div style={{ display: 'grid', gap: 6 }}>
                {TEST_DRIVE_DOCUMENTS.map((d) => (
                  <div key={d.id} style={{
                    display: 'flex', gap: 10, alignItems: 'center',
                    border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '8px 10px',
                  }}>
                    <span aria-hidden style={{ fontSize: 16 }}>📄</span>
                    <span style={{ flex: 1, fontSize: 13 }}>{d.title}</span>
                    {viewed.has(d.id) && (
                      <span className="mono text-xs" style={{ color: 'var(--success, #16a34a)' }}>✓ Viewed</span>
                    )}
                    <button type="button" className="btn small" disabled={busy}
                      onClick={() => { setViewed((s) => new Set(s).add(d.id)); setViewing(d); }}>View</button>
                  </div>
                ))}
              </div>

              {alreadySigned ? (
                <div className="text-xs">
                  ✓ The agreement for this drive is already signed
                  {drive.signerName ? ` by ${drive.signerName}` : drive.agreementAttestedLabel ? ` (attested by ${drive.agreementAttestedLabel})` : ''}.
                </div>
              ) : (
            <>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className={`btn small ${mode === 'SIGN' ? 'primary' : ''}`} onClick={() => setMode('SIGN')} disabled={busy}>
                  ✍ Sign on screen
                </button>
                <button type="button" className={`btn small ${mode === 'PAPER' ? 'primary' : ''}`} onClick={() => setMode('PAPER')} disabled={busy || !!drive}>
                  📝 Paper form signed
                </button>
              </div>

              {mode === 'SIGN' ? (
                <>
                  {tick(accepted && allViewed, setAccepted,
                    `The customer has read and accepts these ${TEST_DRIVE_DOCUMENTS.length} documents.`, !allViewed)}
                  {!allViewed && (
                    <div className="mono text-xs" style={{ color: 'var(--warning)', marginTop: -6 }}>
                      View all {TEST_DRIVE_DOCUMENTS.length} documents to continue ({viewedCount} of {TEST_DRIVE_DOCUMENTS.length} viewed).
                    </div>
                  )}
                  <div className="form-field">
                    <label htmlFor="ci-signer">Customer name *</label>
                    <input id="ci-signer" value={signerName} disabled={busy}
                      onChange={(e) => setSignerName(e.target.value)} placeholder="As shown on the licence" />
                  </div>
                  {sigBlob ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <img src={sigUrl} alt="Captured signature"
                        style={{ height: 70, background: '#fff', borderRadius: 6, border: '1px solid var(--border-color)' }} />
                      <button type="button" className="btn small" disabled={busy} onClick={() => setSigBlob(null)}>Sign again</button>
                    </div>
                  ) : (
                    <SignaturePad ref={padRef} disabled={busy}
                      onChange={setSigEmpty} />
                  )}
                </>
              ) : (
                <>
                  {tick(paperHeld, setPaperHeld, 'A test drive agreement signed by the customer is held on paper.')}
                  <div className="mono text-xs text-muted">
                    Recorded against your name and the time you confirm. Tip: attach a scan of the
                    form below.
                  </div>
                </>
              )}
            </>
              )}

              {/* 3 — anything else the dealership wants on file (optional) */}
              <details style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '8px 10px' }}
                open={docs.length > 0 || (mode === 'PAPER' && !alreadySigned)}>
                <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  Attach other documents (optional){docs.length ? ` · ${docs.length} attached` : ''}
                </summary>
                <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                  <div className="text-xs text-muted">
                    Proof of address, an insurance certificate, a scanned paper form.
                  </div>
                  <label className="btn small" style={{ justifySelf: 'start', cursor: 'pointer' }}>
                    ＋ Add documents
                    <input type="file" multiple hidden accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
                      onChange={addDocs} disabled={busy} />
                  </label>
                  {docs.length === 0 ? (
                    <div className="mono text-xs text-muted">No documents attached.</div>
                  ) : docs.map((d, i) => (
                    <div key={`${d.file.name}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 16 }}>{d.file.type === 'application/pdf' ? '📄' : '🖼'}</span>
                      <input value={d.label} disabled={busy} aria-label="Document label"
                        onChange={(e) => setDocs((all) => all.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                        style={{ flex: 1, padding: '6px 10px', background: 'var(--bg-glass)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: 6 }} />
                      <span className="mono text-xs text-muted">{Math.round(d.file.size / 1024)} KB</span>
                      <button type="button" className="btn small" disabled={busy}
                        onClick={() => setDocs((all) => all.filter((_, j) => j !== i))} aria-label="Remove">✕</button>
                    </div>
                  ))}
                  {tooBig && (
                    <div className="text-xs" style={{ color: 'var(--danger, #c53030)' }}>Files must be 10 MB or smaller.</div>
                  )}
                </div>
              </details>
            </>
          )}

          {step === KEYS_STEP && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-field">
                  <label htmlFor="ci-km">Odometer out (km) *</label>
                  <input id="ci-km" type="number" inputMode="numeric" min={0} value={kmOut} disabled={busy}
                    onChange={(e) => setKmOut(e.target.value)} placeholder="e.g. 41203" autoFocus />
                </div>
                <div className="form-field">
                  <label htmlFor="ci-plate">Trade plate</label>
                  <select id="ci-plate" value={plateId} disabled={busy} onChange={(e) => setPlateId(e.target.value)}>
                    <option value="">None</option>
                    {plates.map((pl) => <option key={pl.id} value={pl.id}>{pl.plateNumber || pl.plate || pl.id}</option>)}
                  </select>
                </div>
              </div>
              {tick(insurance, setInsurance, 'Insurance position declared to the customer')}

              <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 10, fontSize: 12, display: 'grid', gap: 4 }}>
                <div>✓ Licence photo{licBack ? 's (front & back)' : ' (front)'}{licExpiry ? ` · expires ${licExpiry}` : ''}</div>
                <div>✓ {alreadySigned ? 'Agreement already signed' : mode === 'SIGN' ? `${TEST_DRIVE_DOCUMENTS.length} documents accepted · signed on screen by ${signerName.trim()}` : 'Paper agreement attested by you'}</div>
                <div>{docs.length ? `✓ ${docs.length} extra document${docs.length > 1 ? 's' : ''} attached` : '— No extra documents'}</div>
              </div>
              {startingEarly && (
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12,
                  border: '1px solid var(--warning, #d69e2e)', borderRadius: 8, padding: 10,
                  background: 'color-mix(in srgb, var(--warning, #d69e2e) 10%, transparent)' }}>
                  <input type="checkbox" checked={earlyAck} disabled={busy}
                    onChange={(e) => setEarlyAck(e.target.checked)} style={{ marginTop: 2 }} />
                  <span>
                    This drive is booked for{' '}
                    <strong>{new Date(scheduledStart).toLocaleString('en-AU',
                      { timeZone: 'Australia/Sydney', weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</strong>
                    {' '}(AU time) — you're starting it early. Tick to confirm handing over the keys now.
                  </span>
                </label>
              )}
              {progress && <div className="mono text-xs text-muted">{progress}</div>}
            </>
          )}
        </div>

        <div className="modal-footer">
          {step > 0 && <button type="button" className="btn small" disabled={busy} onClick={back}>Back</button>}
          <div style={{ flex: 1 }} />
          <button type="button" className="btn small" disabled={busy} onClick={onClose}>Cancel</button>
          {step < STEPS.length - 1 ? (
            <button type="button" className="btn small primary" disabled={busy || !stepValid[step]} onClick={next}>Next</button>
          ) : (
            <button type="button" className="btn small primary"
              disabled={busy || !stepValid.every(Boolean) || (startingEarly && !earlyAck)} onClick={submit}>
              {busy ? 'Starting…' : '🔑 Hand over the keys'}
            </button>
          )}
        </div>
      </div>

      {/* In-modal document viewer — keeps the check-in on screen (no new tab to
          lose, no pop-up blocker) and means "Viewed" = actually shown here. */}
      {viewing && (
        <div role="dialog" aria-modal="true" aria-label={viewing.title}
          onClick={(e) => { e.stopPropagation(); setViewing(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 320, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(820px, 96vw)', height: 'min(92vh, 900px)', display: 'flex', flexDirection: 'column',
              background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 12,
              boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              borderBottom: '1px solid var(--border-subtle)' }}>
              <span aria-hidden>📄</span>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{viewing.title}</div>
              <a className="btn small" href={viewing.file} target="_blank" rel="noopener noreferrer">Open in new tab</a>
              <button type="button" className="btn small primary" onClick={() => setViewing(null)}>Close</button>
            </div>
            <iframe title={viewing.title} src={viewing.file} style={{ flex: 1, border: 0, background: '#fff' }} />
          </div>
        </div>
      )}
    </div>
  );
}
